import { useCallback, useContext, useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { AppContext } from '../../App'
import store from '../../store'
import { selectActiveChartId } from '../../selectors/chart'
import { selectActiveLayout, selectActiveLayoutId } from '../../selectors/home'
import useSetPrimarySecurity from '../../hooks/useSetPrimarySecurity'
import { useAppContextChart } from '../chartV2/useAppContextChart'
import { registerSurface } from './surfaceRegistry'
import { buildChartSurfaceMetadata, applyChartSurfaceArtifacts } from './domains/chartDomain'
import { resolveContextChartId } from '../chartV2/aiChat/chartContext'
import { WidgetTypes } from '../widgetDashboard/widgetDashboard.enums'
import type { ChartAlertSnapshot } from '../alerts/alertModal.typings'

/** Widget types rendered by ChartWidget — the only ones that register a candle `Chart` instance. */
const CANDLE_WIDGET_TYPES: ReadonlySet<WidgetTypes> = new Set([
    WidgetTypes.CHART,
    WidgetTypes.FUTURE_CHART,
    WidgetTypes.ATM_OPTION_CHART,
    WidgetTypes.OPTION_CHART,
])

export function useChartSurfaceRegistration(isChartRoute: boolean, enabled = true) {
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const appContext = useContext(AppContext)
    const setPrimarySecurity = useSetPrimarySecurity()
    const activeChartId = useSelector(selectActiveChartId)
    const layout = useSelector(selectActiveLayout)
    const layoutId = useSelector(selectActiveLayoutId)
    const contextChartId = useMemo(() => {
        if (!isChartRoute) return ''
        return resolveContextChartId(store.getState(), activeChartId)
    }, [activeChartId, isChartRoute, layout?.activeWidget, layoutId])

    // Charts are attached imperatively to the AppContext ref, so reading `getChart`
    // during render never re-evaluates when one mounts. This hook tracks the instance
    // as state, which is what lets the surface flip to ready after navigation.
    const chart = useAppContextChart(isChartRoute ? contextChartId : '')

    // Multistrike / OI / option-chain panes tear down the candle chart and register into a
    // separate AppContext map, so `getChart` never resolves for them. Gating readiness on a
    // candle instance strands the assistant in those panes — it could not even switch one
    // back to candles, because the orchestrator would keep replying "Opening the chart".
    // Individual command handlers already fail with `chart_unavailable` when they need it.
    const focusedWidgetType = useMemo(() => {
        if (!isChartRoute || !layout || layout.activeWidget == null) return undefined
        return layout.widgets?.[layout.activeWidget]?.widgetType
    }, [isChartRoute, layout, layoutId])
    const needsCandleInstance = focusedWidgetType == null || CANDLE_WIDGET_TYPES.has(focusedWidgetType)
    const ready = Boolean(isChartRoute && contextChartId && (chart || !needsCandleInstance))

    const buildMetadata = useCallback(() => {
        const state = store.getState()
        const chartId = resolveContextChartId(state, activeChartId)
        return buildChartSurfaceMetadata(state, chartId, appContext.getChart.bind(appContext))
    }, [activeChartId, appContext, layout?.activeWidget, layoutId])

    const applyArtifacts = useCallback(
        async (metadata: Record<string, unknown>) => {
            const state = store.getState()
            const chartId = resolveContextChartId(state, activeChartId)
            if (!chartId) {
                return { applied: [], failed: [{ op: 'apply', message: 'Chart not ready' }] }
            }
            const result = await applyChartSurfaceArtifacts(metadata, {
                viewKey: chartId,
                getChart: appContext.getChart.bind(appContext),
                dispatch,
                setPrimarySecurity,
                dashboard: appContext.dashboard,
                navigate,
                getChartSnapshot: (targetViewKey?: string) => {
                    const method = appContext.getChartSnapshot?.[targetViewKey || chartId]
                    return (method?.() as ChartAlertSnapshot | undefined) ?? null
                },
            })
            return result ?? { applied: [], failed: [] }
        },
        [activeChartId, appContext, dispatch, layout?.activeWidget, layoutId, navigate, setPrimarySecurity],
    )

    useEffect(() => {
        if (!enabled || !isChartRoute) return
        return registerSurface({
            domain: 'chart',
            ready,
            buildMetadata,
            applyArtifacts,
        })
    }, [applyArtifacts, buildMetadata, enabled, isChartRoute, ready])
}
